/*
 * Taken from https://github.com/joshjg/react-canvas-knob/tree/a7c69ccc826222569ae5e0d9319cecd3d11b4489
 */

import React, { useRef, useEffect } from 'react';

const ANGLE_ARC = (360 * Math.PI) / 180;

const START_ANGLE = 1.5 * Math.PI;

const END_ANGLE = 1.5 * Math.PI + ANGLE_ARC;

const getArcToValue = (v: number, min: number, max: number) => {
  let startAngle = 0;
  let endAngle = 0;

  const angle = ((v - min) * ANGLE_ARC) / (max - min);

  startAngle = START_ANGLE - 0.00001;
  endAngle = startAngle + angle + 0.00001;

  startAngle = endAngle - 0.3;
  endAngle += 0.3;

  return { startAngle, endAngle };
};

const getCanvasScale = () => {
  return (window.devicePixelRatio || 1) / 1;
};

const coerceToStep = (v: number, min: number, max: number) => {
  let val = ~~((v < 0 ? -0.5 : 0.5) + v);

  val = Math.max(Math.min(val, max), min);

  if (isNaN(val)) val = 0;

  return Math.round(val * 1000) / 1000;
};

interface KnobProps {
  value: number;
  min?: number;
  max?: number;
  width?: number;
  height?: number;
  thickness?: number;
  fgColor?: string;
  bgColor?: string;
  onChange: (n: number) => void;
}

const Knob: React.FC<KnobProps> = ({
  value,
  min = 0,
  max = 100,
  width = 200,
  height = 200,
  thickness = 0.35,
  fgColor = '#EA2',
  bgColor = '#EEE',
  onChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');

    if (ctx && canvasRef.current) {
      const scale = getCanvasScale();

      canvasRef.current.width = width * scale;
      canvasRef.current.height = height * scale;

      ctx.scale(scale, scale);

      const xy = width / 2;
      const lineWidth = xy * thickness;
      const radius = xy - lineWidth / 2;

      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'butt';
      ctx.beginPath();
      ctx.strokeStyle = bgColor;
      ctx.arc(xy, xy, radius, END_ANGLE - 0.00001, START_ANGLE + 0.00001, true);
      ctx.stroke();

      const a = getArcToValue(value, min, max);

      ctx.beginPath();
      ctx.strokeStyle = fgColor;
      ctx.arc(xy, xy, radius, a.startAngle, a.endAngle, false);
      ctx.stroke();
    }
  }, [min, max, value, width, height, fgColor, bgColor, thickness]);

  const eventToValue = (e: MouseEvent | React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    const bounds = canvasRef.current?.getBoundingClientRect() as DOMRect;

    const x = e.clientX - bounds.left;
    const y = e.clientY - bounds.top;

    let a = Math.atan2(x - width / 2, width / 2 - y);

    if (ANGLE_ARC !== Math.PI * 2 && a < 0 && a > -0.5) {
      a = 0;
    } else if (a < 0) {
      a += Math.PI * 2;
    }

    const val = (a * (max - min)) / ANGLE_ARC + min;

    return coerceToStep(val, min, max);
  };

  const handleMouseMove = (e: MouseEvent) => {
    e.preventDefault();

    onChange(eventToValue(e));
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    onChange(eventToValue(e));

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div style={{ width, height, display: 'inline-block' }}>
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default Knob;
